import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { HeaderRow, ImportData } from './importer-types'
import {
  ColumnOptionKey,
  columnOptions,
  getColumnOption,
} from './ImporterViewModel'
import { ColumnDef } from '@tanstack/react-table'
import { BaseCheckbox } from 'components/FormUtils/Checkbox/checkbox'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { Switch } from 'components/UI/Switch/Switch'
import { AlertTriangle, Building2 } from 'lucide-react'

// Auto-matching configuration
const AUTO_MATCH_PATTERNS: Record<ColumnOptionKey, string[]> = {
  // Primary Keys
  company_opp_type: ['company_type', 'company type', 'business_type', 'type'],
  firstbite_id: ['firstbite', 'fb_id', 'chain_id', 'company_id'],
  door_id: ['door', 'location_id', 'store_id', 'restaurant_id'],

  // Company Match Fields
  company_name: [
    'company_name',
    'company',
    'business_name',
    'business',
    'restaurant_name',
    'restaurant',
    'chain_name',
    'chain',
    'name',
  ],
  company_phone: [
    'company_phone',
    'business_phone',
    'phone',
    'telephone',
    'tel',
    'phone_number',
  ],
  company_domain: [
    'company_domain',
    'domain',
    'website',
    'url',
    'web_address',
    'site',
  ],
  company_address_street: [
    'company_address',
    'business_address',
    'address',
    'street_address',
    'street',
    'addr',
  ],
  company_address_street_2: [
    'address_2',
    'address2',
    'suite',
    'unit',
    'apt',
    'apartment',
  ],
  company_address_city: ['company_city', 'business_city', 'city', 'town'],
  company_address_state: [
    'company_state',
    'business_state',
    'state',
    'province',
    'region',
  ],
  company_address_zip: [
    'company_zip',
    'business_zip',
    'zip_code',
    'zip',
    'postal_code',
    'postal',
    'postcode',
  ],
  company_address_country: [
    'company_country',
    'business_country',
    'country',
    'nation',
  ],
  company_full_address: ['full_address', 'complete_address', 'mailing_address'],

  // Contact Fields
  contact_first_name: ['first_name', 'first', 'fname', 'given_name', 'given'],
  contact_last_name: [
    'last_name',
    'last',
    'lname',
    'surname',
    'family_name',
    'family',
  ],
  contact_full_name: ['full_name', 'contact_name', 'name'],
  contact_phone: ['contact_phone', 'phone', 'mobile', 'cell', 'telephone'],
  contact_work_email: ['work_email', 'business_email', 'email', 'work_mail'],
  contact_personal_email: ['personal_email', 'private_email', 'personal_mail'],
  contact_generic_email: ['generic_email', 'info_email', 'general_email'],
  contact_title: ['job_title', 'title', 'position', 'role', 'designation'],
  contact_linkedin_url: [
    'linkedin',
    'li_url',
    'linkedin_url',
    'linkedin_profile',
  ],
  contact_label: ['contact_label', 'label', 'contact_type', 'type', 'category'],

  // Deal Fields
  deal_sales_stage: ['stage', 'sales_stage', 'pipeline', 'deal_stage'],
  deal_account_owner: [
    'owner',
    'account_owner',
    'sales_rep',
    'owner_email',
    'account_owner_email',
    'rep_email',
  ],
  deal_product: ['product', 'service', 'offering', 'deal_product'],

  // Other
  tag_name: ['tag', 'tags', 'segment', 'company tag', 'company_tag'],
  company_notes: [
    'company_notes',
    'notes',
    'comments',
    'remarks',
    'description',
  ],
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix = []

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        )
      }
    }
  }

  return matrix[str2.length][str1.length]
}

function stringSimilarity(str1: string, str2: string): number {
  const maxLength = Math.max(str1.length, str2.length)
  if (maxLength === 0) return 1

  const distance = levenshteinDistance(str1, str2)
  return 1 - distance / maxLength
}

function autoMatchHeader(headerName: string): string {
  const normalizedHeader = headerName
    .toLowerCase()
    .replace(/ /g, '_') // Replace spaces with underscore
    .replace(/[^a-z0-9]/g, '_') // Replace special chars with underscore
    .replace(/_+/g, '_') // Collapse multiple underscores
    .replace(/^_|_$/g, '') // Trim leading/trailing underscores

  // Try exact keyword matches first
  for (const [columnKey, patterns] of Object.entries(AUTO_MATCH_PATTERNS)) {
    if (patterns.some((pattern) => normalizedHeader === pattern)) {
      return columnKey
    }
  }

  // Try fuzzy matching with string similarity
  let bestMatch = 'ignore'
  let bestScore = 0.6 // Minimum confidence threshold

  for (const [columnKey, patterns] of Object.entries(AUTO_MATCH_PATTERNS)) {
    for (const pattern of patterns) {
      const score = stringSimilarity(normalizedHeader, pattern)
      if (score > bestScore) {
        bestScore = score
        bestMatch = columnKey
      }
    }
  }

  return bestMatch
}

export interface ImporterColumnAssignModalProps {
  data: ImportData | null
  open: boolean
  onClose: () => void
  onSave: (updatedData: ImportData, skipMatching: boolean) => void
}

function autoMatchWithConflictResolution(headers: HeaderRow[]): HeaderRow[] {
  const matches: Array<{
    header: HeaderRow
    column: string
    originalIndex: number
  }> = []

  // Get all possible matches
  headers.forEach((header, index) => {
    const column = autoMatchHeader(header.original)
    matches.push({ header, column, originalIndex: index })
  })

  // Sort by priority: non-ignore matches first, then by original order
  const finalMatches = new Map<string, string>()
  const usedColumns = new Set<string>()

  // First pass: handle non-ignore matches
  matches
    .filter((match) => match.column !== 'ignore')
    .forEach((match) => {
      if (!usedColumns.has(match.column) || match.column === 'company_notes') {
        finalMatches.set(match.header.original, match.column)
        if (match.column !== 'company_notes') {
          usedColumns.add(match.column)
        }
      } else {
        finalMatches.set(match.header.original, 'ignore')
      }
    })

  // Second pass: handle ignore matches
  matches
    .filter((match) => match.column === 'ignore')
    .forEach((match) => {
      finalMatches.set(match.header.original, 'ignore')
    })

  return headers.map((header) => ({
    ...header,
    mapped: finalMatches.get(header.original) || 'ignore',
  }))
}

export function ImporterColumnAssignModal({
  data,
  open,
  onClose,
  onSave,
}: ImporterColumnAssignModalProps) {
  const [localHeaders, setLocalHeaders] = useState<HeaderRow[]>([])
  const [skipMatching, setSkipMatching] = useState(false)

  // Initialize with auto-matched headers when data changes
  useEffect(() => {
    if (data?.headers) {
      const autoMatchedHeaders = autoMatchWithConflictResolution(
        data.headers.map((h) => ({ ...h }))
      )
      setLocalHeaders(autoMatchedHeaders)
    }
  }, [data])

  const hasChanges = useMemo(() => {
    return localHeaders.some(
      (localHeader, index) =>
        localHeader.mapped !== data?.headers[index]?.mapped
    )
  }, [localHeaders, data?.headers])

  const hasCompanyNameMapped = useMemo(() => {
    return localHeaders.some((header) => header.mapped === 'company_name')
  }, [localHeaders])

  const isSkipMatchingValid = useMemo(() => {
    return !skipMatching || hasCompanyNameMapped
  }, [skipMatching, hasCompanyNameMapped])

  const companyMatchColumns = useMemo(() => {
    return localHeaders
      .filter(
        (header) =>
          getColumnOption(header.mapped)?.type === 'company_match' ||
          getColumnOption(header.mapped)?.type === 'primary_key'
      )
      .map((header) => header.original)
  }, [localHeaders])

  const updateLocalMap = (original_header: string, mapped_column: string) => {
    setLocalHeaders((prev) =>
      prev.map((header) =>
        header.original === original_header
          ? { ...header, mapped: mapped_column }
          : header
      )
    )
  }

  const updateExclude = (original_header: string, exclude: boolean) => {
    setLocalHeaders((prev) =>
      prev.map((header) =>
        header.original === original_header
          ? {
              ...header,
              mapped: exclude
                ? 'ignore'
                : header.mapped === 'ignore'
                  ? ''
                  : header.mapped,
            }
          : header
      )
    )
  }

  const handleSave = () => {
    const updatedData: ImportData = {
      ...data!,
      headers: localHeaders,
    }
    onSave(updatedData, skipMatching)
  }

  const handleCancel = () => {
    if (data?.headers) {
      const autoMatchedHeaders = autoMatchWithConflictResolution(
        data.headers.map((h) => ({ ...h }))
      )
      setLocalHeaders(autoMatchedHeaders)
    }
    onClose()
  }

  // Track which columns are already mapped to prevent duplicates
  const mappedColumns = useMemo(() => {
    return localHeaders
      .map((h) => h.mapped)
      .filter((mapped) => mapped !== 'ignore') // Allow multiple 'ignore' mappings
  }, [localHeaders])

  // Check for contact name column conflict
  const hasContactNameConflict = useMemo(() => {
    const hasFullName = mappedColumns.includes('contact_full_name')
    const hasFirstName = mappedColumns.includes('contact_first_name')
    const hasLastName = mappedColumns.includes('contact_last_name')
    return hasFullName && (hasFirstName || hasLastName)
  }, [mappedColumns])

  const isValid = useMemo(() => {
    const allMapped = localHeaders.every(
      (header) =>
        header.mapped === 'ignore' ||
        (header.mapped && header.mapped.trim() !== '')
    )
    return allMapped && !hasContactNameConflict
  }, [localHeaders, hasContactNameConflict])

  // Create options for the select
  const selectOptions = useMemo(() => {
    const hasFullName = mappedColumns.includes('contact_full_name')
    const hasFirstOrLastName =
      mappedColumns.includes('contact_first_name') ||
      mappedColumns.includes('contact_last_name')

    return Object.entries(columnOptions).map(([k, v]) => {
      // Disable contact_full_name if first_name or last_name is already mapped
      const isFullNameDisabledByConflict =
        k === 'contact_full_name' && hasFirstOrLastName
      // Disable contact_first_name or contact_last_name if full_name is already mapped
      const isPartialNameDisabledByConflict =
        (k === 'contact_first_name' || k === 'contact_last_name') && hasFullName

      return {
        label: v.name,
        value: k,
        // Hide if already mapped by another header (but allow company_notes to be reused)
        // Also disable conflicting contact name columns
        isDisabled:
          (mappedColumns.includes(k) && k !== 'company_notes') ||
          isFullNameDisabledByConflict ||
          isPartialNameDisabledByConflict,
      }
    })
  }, [mappedColumns])

  const columns: ColumnDef<HeaderRow>[] = useMemo(
    () => [
      {
        header: 'Header Name',
        accessorKey: 'original',
        size: 250,
        cell: (info) => {
          const header = info.row.original
          const wasAutoMatched =
            data?.headers.find((h) => h.original === header.original)
              ?.mapped !== header.mapped
          const isCompanyColumn = companyMatchColumns.includes(header.original)
          const columnType = getColumnOption(header.mapped)?.type

          return (
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <div className="font-medium text-sm">
                  {header.label || header.original}
                </div>
                {wasAutoMatched && header.mapped !== 'ignore' && (
                  <div className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    Auto-matched
                  </div>
                )}
                {isCompanyColumn && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                    <Building2 size={12} />
                    <span>Company</span>
                  </div>
                )}
              </div>
              {header.label && (
                <div className="text-xs text-gray-500 truncate">
                  {header.original}
                </div>
              )}
              {columnType && columnType !== 'ignore' && (
                <div className="text-xs text-gray-400 mt-0.5">
                  {getColumnOption(header.mapped)?.name}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: 'Mapped Column',
        accessorKey: 'mapped',
        size: 300,
        cell: (info) => {
          const header = info.row.original
          const isExcluded = header.mapped === 'ignore'
          const selectedOption = selectOptions.find(
            (opt) => opt.value === header.mapped
          )

          return (
            <ReactSelectWrapper
              value={selectedOption || null}
              onChange={(option: any) => {
                updateLocalMap(header.original, option?.value || '')
              }}
              options={selectOptions}
              placeholder="Select a Column"
              isClearable
              isDisabled={isExcluded}
              styles={{
                container: (provided: any) => ({
                  ...provided,
                  width: '100%',
                }),
              }}
            />
          )
        },
      },
      {
        header: 'Exclude',
        accessorKey: 'exclude',
        size: 80,
        cell: (info) => {
          const header = info.row.original
          const isExcluded = header.mapped === 'ignore'

          return (
            <div className="flex items-center justify-center h-full">
              <BaseCheckbox
                name={`exclude_${header.original}`}
                value={isExcluded}
                onChange={(exclude: boolean) =>
                  updateExclude(header.original, exclude)
                }
                style={{ justifyContent: 'center' }}
              />
            </div>
          )
        },
      },
    ],
    [localHeaders, mappedColumns, selectOptions, companyMatchColumns]
  )

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (hasChanges) {
            if (
              confirm(
                'You have unsaved changes. Are you sure you want to close?'
              )
            ) {
              handleCancel()
            }
          } else {
            onClose()
          }
        }
      }}
      title="Update Header Mapping"
      description={
        <>
          Configure how your CSV headers map to system fields. Headers have been
          automatically matched where possible - review and adjust as needed.
        </>
      }
      footerChildren={
        <>
          {hasContactNameConflict && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              Contact Full Name cannot be used together with Contact First Name
              or Contact Last Name. Please use one or the other.
            </div>
          )}
          {!isValid && !hasContactNameConflict && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              All headers must either be mapped to a column or excluded.
            </div>
          )}
        </>
      }
      size="lg"
      acceptButtonText="Save Changes"
      onAccept={handleSave}
      onCancel={handleCancel}
      blockAccept={!hasChanges || !isValid || !isSkipMatchingValid}
    >
      {/* Skip Matching Toggle */}
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-amber-900">
                Skip Company Matching
              </span>
              <Switch
                checked={skipMatching}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSkipMatching(true)
                  } else {
                    setSkipMatching(false)
                  }
                }}
              />
            </div>
            <p className="text-sm text-amber-800">
              When enabled, all companies will be imported as custom
              (user-generated) companies. No matching against the First Bite
              database will be performed. This is useful when you only need to
              import tags, deals, or contacts without worrying about company
              matching.
            </p>
            {skipMatching && !hasCompanyNameMapped && (
              <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-red-800 text-sm">
                <strong>Required:</strong> You must map at least the
                &quot;Company Name&quot; column to create custom companies.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-3 flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-100 rounded"></div>
          <span>Company column (used for matching/custom creation)</span>
        </div>
      </div>

      <ClientDataTable
        data={localHeaders}
        columns={columns}
        height={500}
        enableSorting={false}
      />
    </Modal>
  )
}
